import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';
import {
  SyncConfig,
  SyncConfigForm,
  SyncHistory,
  SyncPreview,
  SyncRequest,
  TableGroup,
  ConnectionTestResult,
  Account,
  MenuItem
} from '../types';

// Create a service-specific logger
const log = logger.getLogger('RegionSyncService');

const baseUrl = environment.services.regionSync?.baseUrl || environment.services.account.baseUrl;
const regionSyncUrl = baseUrl + (environment.services.regionSync?.basePath || '/region-sync');

class RegionSyncService {
  // ============= Config Management =============

  /**
   * Get all sync configurations
   */
  async getConfigs(): Promise<SyncConfig[]> {
    try {
      log.log('Fetching sync configurations');
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/api/config`);
      // API returns { success, configs, count } - extract the configs array
      return response.configs || response.data || response || [];
    } catch (error) {
      log.error('Error fetching configs:', error);
      throw error;
    }
  }

  /**
   * Get a single sync configuration by ID
   */
  async getConfig(id: number): Promise<SyncConfig> {
    try {
      log.log(`Fetching config with ID ${id}`);
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/api/config/${id}`);
      // API returns { success, config }
      return response.config || response.data || response;
    } catch (error) {
      log.error(`Error fetching config ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get available environments with Cloud SQL instance IPs and K8s secret names
   */
  async getEnvironments(): Promise<any> {
    try {
      log.log('Fetching available environments');
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/api/config/environments`);
      return response.environments || response.data?.environments || [];
    } catch (error) {
      log.error('Error fetching environments:', error);
      return [];
    }
  }

  /**
   * Create a new sync configuration
   */
  async createConfig(config: SyncConfigForm): Promise<SyncConfig> {
    try {
      log.log('Creating new sync configuration', { name: config.name });
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/config`, config);
      apiService.invalidateCacheByPattern('/api/config');
      // API returns { success, config }
      return response.config || response.data || response;
    } catch (error) {
      log.error('Error creating config:', error);
      throw error;
    }
  }

  /**
   * Update an existing sync configuration
   */
  async updateConfig(id: number, config: Partial<SyncConfigForm>): Promise<SyncConfig> {
    try {
      log.log(`Updating config with ID ${id}`);
      const response = await apiService.makeRequest('put', `${regionSyncUrl}/api/config/${id}`, config);
      apiService.invalidateCacheByPattern('/api/config');
      // API returns { success, config }
      return response.config || response.data || response;
    } catch (error) {
      log.error(`Error updating config ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a sync configuration
   */
  async deleteConfig(id: number): Promise<void> {
    try {
      log.log(`Deleting config with ID ${id}`);
      await apiService.makeRequest('delete', `${regionSyncUrl}/api/config/${id}`);
      apiService.invalidateCacheByPattern('/api/config');
    } catch (error) {
      log.error(`Error deleting config ${id}:`, error);
      throw error;
    }
  }

  /**
   * Test database connection for a configuration
   */
  async testConnection(id: number): Promise<ConnectionTestResult> {
    try {
      log.log(`Testing connection for config ${id}`);
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/config/${id}/test`);
      return response.data || response;
    } catch (error) {
      log.error(`Error testing connection for config ${id}:`, error);
      throw error;
    }
  }

  /**
   * Test database connection with raw parameters (without saving)
   */
  async testConnectionRaw(params: {
    dbHost: string;
    dbPort: number;
    dbName: string;
    dbUsername?: string;
    dbPassword?: string;
    k8sSecretName?: string;
    k8sSecretNamespace?: string;
    useSsl?: boolean;
  }): Promise<ConnectionTestResult> {
    try {
      log.log('Testing raw connection', { host: params.dbHost, db: params.dbName });
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/config/test-connection`, params);
      return response.data || response;
    } catch (error: any) {
      log.error('Error testing raw connection:', error);
      // Extract the error message from the API response if available
      const message = error?.response?.data?.error || error?.message || 'Connection test failed';
      return { success: false, message };
    }
  }

  // ============= Sync Operations =============

  /**
   * Get available table groups for syncing
   */
  async getTableGroups(): Promise<TableGroup[]> {
    try {
      log.log('Fetching table groups');
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/api/sync/groups`);
      // API returns { success, groups }
      return response.groups || response.data || response || [];
    } catch (error) {
      log.error('Error fetching table groups:', error);
      throw error;
    }
  }

  /**
   * Preview sync operation (dry run)
   */
  async previewSync(request: SyncRequest): Promise<SyncPreview> {
    try {
      log.log('Previewing sync operation', request);
      const apiBody = {
        sourceConfigId: request.sourceConfigId,
        targetConfigId: request.targetConfigId,
        source: request.source,
        target: request.target,
        options: { ...request.options, dryRun: true }
      };
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/sync/preview`, apiBody);
      return response.data || response;
    } catch (error) {
      log.error('Error previewing sync:', error);
      throw error;
    }
  }

  /**
   * Execute sync operation
   */
  async executeSync(request: SyncRequest): Promise<SyncHistory> {
    try {
      log.log('Executing sync operation', request);
      const apiBody = {
        syncName: request.syncName,
        sourceConfigId: request.sourceConfigId,
        targetConfigId: request.targetConfigId,
        source: request.source,
        target: request.target,
        options: { ...request.options, dryRun: false }
      };
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/sync/full-copy`, apiBody);
      apiService.invalidateCacheByPattern('/api/sync/history');
      return response.data || response;
    } catch (error) {
      log.error('Error executing sync:', error);
      throw error;
    }
  }

  /**
   * Execute a solution-only copy (always createNewIds, no menu)
   */
  async executeSolutionCopy(request: SyncRequest): Promise<SyncHistory> {
    try {
      log.log('Executing solution copy', request);
      const apiBody = {
        syncName: request.syncName,
        sourceConfigId: request.sourceConfigId,
        targetConfigId: request.targetConfigId,
        source: request.source,
        target: request.target,
        options: { ...request.options, dryRun: false }
      };
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/sync/solution-copy`, apiBody);
      apiService.invalidateCacheByPattern('/api/sync/history');
      return response.data || response;
    } catch (error) {
      log.error('Error executing solution copy:', error);
      throw error;
    }
  }

  /**
   * Get solutions for an account from a specific config
   */
  async getSolutions(configId: number, accountId: number): Promise<any[]> {
    try {
      log.log(`Fetching solutions for account ${accountId} from config ${configId}`);
      const response = await apiService.makeRequest(
        'get',
        `${regionSyncUrl}/api/sync/solutions?configId=${configId}&accountId=${accountId}`
      );
      return response.solutions || response.data || [];
    } catch (error) {
      log.error('Error fetching solutions:', error);
      throw error;
    }
  }

  /**
   * Get menus for an account from a specific config
   */
  async getMenus(configId: number, accountId: number): Promise<MenuItem[]> {
    try {
      log.log(`Fetching menus for account ${accountId} from config ${configId}`);
      const response = await apiService.makeRequest(
        'get',
        `${regionSyncUrl}/api/sync/menus?configId=${configId}&accountId=${accountId}`
      );
      return response.data || response || [];
    } catch (error) {
      log.error('Error fetching menus:', error);
      throw error;
    }
  }

  // ============= Sync History =============

  /**
   * Get sync history
   */
  async getHistory(params?: { page?: number; size?: number; status?: string }): Promise<{ data: SyncHistory[]; count: number }> {
    try {
      // API expects limit/offset, not page/size — translate
      const limit = params?.size || 50;
      const offset = (params?.page || 0) * limit;
      let url = `${regionSyncUrl}/api/sync/history?limit=${limit}&offset=${offset}`;
      if (params?.status) url += `&status=${params.status}`;

      log.log('Fetching sync history', params);
      const response = await apiService.makeRequest('get', url);

      // API returns { success, history, count, ...options }
      const historyData = response.history || response.data || response || [];
      return {
        data: Array.isArray(historyData) ? historyData : [],
        count: response.count || response.total || (Array.isArray(historyData) ? historyData.length : 0)
      };
    } catch (error) {
      log.error('Error fetching history:', error);
      throw error;
    }
  }

  /**
   * Get details of a specific sync operation
   */
  async getHistoryDetail(id: number | string): Promise<SyncHistory> {
    try {
      log.log(`Fetching sync detail for ${id}`);
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/api/sync/details/${id}`);
      // API returns { success, details }
      return response.details || response.data || response;
    } catch (error) {
      log.error(`Error fetching history detail ${id}:`, error);
      throw error;
    }
  }

  /**
   * Rollback a sync operation
   */
  async rollbackSync(historyId: number): Promise<SyncHistory> {
    try {
      log.log(`Rolling back sync operation ${historyId}`);
      const response = await apiService.makeRequest('post', `${regionSyncUrl}/api/sync/rollback/${historyId}`);
      apiService.invalidateCacheByPattern('/api/sync/history');
      return response.data || response;
    } catch (error) {
      log.error(`Error rolling back sync ${historyId}:`, error);
      throw error;
    }
  }

  // ============= Helper Methods =============

  /**
   * Get accounts for dropdown selection
   */
  async getAccounts(configId: number): Promise<Account[]> {
    try {
      log.log(`Fetching accounts from config ${configId}`);
      const response = await apiService.makeRequest(
        'get',
        `${regionSyncUrl}/api/sync/accounts?configId=${configId}`
      );
      return response.data || response || [];
    } catch (error) {
      log.error('Error fetching accounts:', error);
      // Fallback to account service
      try {
        const accountUrl = environment.services.account.baseUrl + environment.services.account.basePath;
        const fallbackResponse = await apiService.makeRequest('get', `${accountUrl}/accounts/?page=0&size=1000`);
        const accounts = fallbackResponse.data || fallbackResponse.accounts?.items || [];
        return accounts.map((a: any) => ({ id: a.id, name: a.name }));
      } catch (fallbackError) {
        log.error('Fallback account fetch also failed:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Health check for the region sync service
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await apiService.makeRequest('get', `${regionSyncUrl}/health`);
      return response;
    } catch (error) {
      log.error('Health check failed:', error);
      throw error;
    }
  }
}

export const regionSyncService = new RegionSyncService();
export default regionSyncService;
